/**
 * CrmSchedulingTypeFilter - Filtro de tipo de agendamento
 * 
 * Permite selecionar: Todos Tipos, Agendamento Online, Reunião ou Tarefa.
 * Segue o padrão visual do CrmActivityStatusFilter.
 * 
 * @version 1.0.0
 * RELEASE: 2026-10-05 v1.1.0 - Gatilho do filtro segue o modelo de pill: sem a linha cinza da borda, fundo de superfície, sombra sutil e cantos arredondados (rounded-full).
 * RELEASE: 2026-02-14 v1.0.0 - Implementação inicial
 */

import React, { useState } from 'react';
import { Check, ChevronDown, LayoutList, Users, ClipboardList } from 'lucide-react';
import googleMeetIcon from '@/assets/google-meet-icon.png';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';

/**
 * Tipo para filtro de tipo de agendamento
 * - null: todos os tipos
 * - 'online': reuniões com link de videoconferência
 * - 'meeting': reunião presencial
 * - 'task': tarefa
 */
export type SchedulingTypeFilter = null | 'online' | 'meeting' | 'task';

/**
 * Props do componente CrmSchedulingTypeFilter
 */
interface CrmSchedulingTypeFilterProps {
  /** Tipo selecionado */
  value: SchedulingTypeFilter;
  /** Callback para mudança de seleção */
  onChange: (type: SchedulingTypeFilter) => void;
  /** Classes CSS adicionais */
  className?: string;
}

/**
 * Opções de tipo disponíveis
 */
const TYPE_OPTIONS: { value: SchedulingTypeFilter; label: string; icon: typeof LayoutList | null; customIcon?: string }[] = [
  { value: null, label: 'Todos Tipos', icon: LayoutList },
  { value: 'online', label: 'Agend. Online', icon: null, customIcon: googleMeetIcon },
  { value: 'meeting', label: 'Reunião', icon: Users },
  { value: 'task', label: 'Lembrete', icon: ClipboardList },
];

/**
 * Componente de filtro de tipo de agendamento
 */
export function CrmSchedulingTypeFilter({
  value,
  onChange,
  className,
}: CrmSchedulingTypeFilterProps) {
  const [open, setOpen] = useState(false);

  // Opção selecionada
  const selectedOption = TYPE_OPTIONS.find(o => o.value === value) || TYPE_OPTIONS[0];

  // Handler para seleção
  const handleSelect = (type: SchedulingTypeFilter) => {
    onChange(type);
    setOpen(false);
  };

  // Detecta se é versão compacta baseado na classe
  const isCompact = className?.includes('h-[22px]');

  // Ícone dinâmico
  const SelectedIcon = selectedOption.icon;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'h-9 px-3 gap-2 font-normal border-0 bg-background rounded-full',
            className
          )}
        >
          {selectedOption.customIcon
            ? <img src={selectedOption.customIcon} alt={selectedOption.label} className={cn(isCompact ? 'h-3 w-3' : 'h-4 w-4', 'opacity-70')} />
            : SelectedIcon && <SelectedIcon className={cn(isCompact ? 'h-3 w-3' : 'h-4 w-4', 'opacity-70')} />
          }
          <span className="hidden sm:inline truncate">{selectedOption.label}</span>
          <ChevronDown className={cn(isCompact ? 'h-2.5 w-2.5' : 'h-3 w-3', 'opacity-50')} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0" align="start">
        <Command>
          <CommandList>
            <CommandGroup heading="Tipo de Agendamento">
              {TYPE_OPTIONS.map(option => {
                const OptionIcon = option.icon;
                // Usa string segura como value do CommandItem
                const itemValue = option.value ?? 'all';
                return (
                  <CommandItem
                    key={itemValue}
                    value={itemValue}
                    onSelect={() => handleSelect(option.value)}
                    className="cursor-pointer"
                  >
                    <div
                      className={cn(
                        "mr-2 h-4 w-4 rounded-full border flex items-center justify-center",
                        value === option.value
                          ? "border-primary bg-primary"
                          : "border-muted"
                      )}
                    >
                      {value === option.value && (
                        <Check className="h-2.5 w-2.5 text-primary-foreground" />
                      )}
                    </div>
                    {option.customIcon
                      ? <img src={option.customIcon} alt={option.label} className="h-4 w-4 mr-2 opacity-70" />
                      : OptionIcon && <OptionIcon className="h-4 w-4 mr-2 opacity-70" />
                    }
                    <span>{option.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
